import React, { useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';

interface Notification {
  id: string;
  type: 'aguardando' | 'acao-necessaria' | 'reprovado' | 'aprovado' | 'dimensa';
  title: string;
  description: string;
  timestamp: string;
  onClick?: () => void;
}

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'notifications' | 'unread'>('notifications');

  const mockNotifications: Notification[] = [
    {
      id: '1',
      type: 'aguardando',
      title: 'FIDUCIA SCM',
      description: 'A análise enviada há 37 horas.',
      timestamp: 'A algumas semana(s) 37 horas',
    },
    {
      id: '2',
      type: 'aguardando',
      title: 'FIDUCIA SCM',
      description: 'A análise enviada há 37 horas.',
      timestamp: 'A algumas semana(s) 37 horas',
    },
    {
      id: '3',
      type: 'acao-necessaria',
      title: 'FIDUCIA SCM',
      description: 'A análise enviada há 37 horas.',
      timestamp: 'A algumas semana(s) 37 horas',
    },
    {
      id: '4',
      type: 'acao-necessaria',
      title: 'FIDUCIA SCM',
      description: 'A análise enviada há 37 horas.',
      timestamp: 'A algumas semana(s) 37 horas',
    },
    {
      id: '5',
      type: 'reprovado',
      title: 'FIDUCIA SCM',
      description: 'A análise enviada há 37 horas.',
      timestamp: 'A algumas semana(s) 37 horas',
    },
    {
      id: '6',
      type: 'reprovado',
      title: 'FIDUCIA SCM',
      description: 'A análise enviada há 37 horas.',
      timestamp: 'A algumas semana(s) 37 horas',
    },
    {
      id: '7',
      type: 'dimensa',
      title: 'FIDUCIA SCM',
      description: 'A análise enviada há 37 horas.',
      timestamp: 'há 3h21',
    },
    {
      id: '8',
      type: 'dimensa',
      title: 'FIDUCIA SCM',
      description: 'A análise enviada há 37 horas.',
      timestamp: 'há 3h21',
    },
  ];

  const getBadgeStyles = (type: Notification['type']) => {
    switch (type) {
      case 'aguardando':
        return 'bg-yellow-400 text-yellow-900';
      case 'acao-necessaria':
        return 'bg-orange-400 text-orange-900';
      case 'reprovado':
        return 'bg-red-400 text-red-900';
      case 'aprovado':
        return 'bg-green-400 text-green-900';
      case 'dimensa':
        return 'bg-purple-400 text-purple-900';
      default:
        return 'bg-neutral-400 text-neutral-900';
    }
  };

  const getBackgroundStyles = (type: Notification['type']) => {
    switch (type) {
      case 'aguardando':
        return 'bg-yellow-50 border-yellow-200';
      case 'acao-necessaria':
        return 'bg-orange-50 border-orange-200';
      case 'reprovado':
        return 'bg-red-50 border-red-200';
      case 'aprovado':
        return 'bg-green-50 border-green-200';
      case 'dimensa':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-neutral-50 border-neutral-200';
    }
  };

  const getBadgeLabel = (type: Notification['type']) => {
    switch (type) {
      case 'aguardando':
        return 'Nova análise';
      case 'acao-necessaria':
        return 'Ação necessária';
      case 'reprovado':
        return 'Reprovado';
      case 'aprovado':
        return 'Aprovado';
      case 'dimensa':
        return 'Dimensa';
      default:
        return 'Notificação';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-neutral-200 z-50 max-h-[600px] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-neutral-200">
          <h3 className="text-lg font-bold text-neutral-800 flex items-center gap-2">
            <span className="text-purple-600">✦</span> Notificações
          </h3>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200 px-4">
          <button
            type="button"
            onClick={() => { setActiveTab('notifications'); }}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === 'notifications'
                ? 'text-purple-600'
                : 'text-neutral-600 hover:text-neutral-800'
            }`}
          >
            Notificações
            {activeTab === 'notifications' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />
            )}
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('unread'); }}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === 'unread'
                ? 'text-purple-600'
                : 'text-neutral-600 hover:text-neutral-800'
            }`}
          >
            Limpar notificações
            {activeTab === 'unread' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />
            )}
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {mockNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${getBackgroundStyles(
                notification.type
              )}`}
              onClick={notification.onClick}
            >
              <div className="flex items-start justify-between mb-2">
                <span
                  className={`px-2 py-1 rounded text-xs font-bold ${getBadgeStyles(
                    notification.type
                  )}`}
                >
                  {getBadgeLabel(notification.type)}
                </span>
                <FiChevronRight className="w-4 h-4 text-neutral-500 shrink-0" />
              </div>
              <h4 className="text-sm font-bold text-neutral-800 mb-1">{notification.title}</h4>
              <p className="text-xs text-neutral-600 mb-2">{notification.description}</p>
              <p className="text-xs text-neutral-500">{notification.timestamp}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
